import React, { useState, useMemo } from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { 
  CopyIcon, 
  CheckCircle as CopyCheck, 
  ChevronDown, 
  ChevronUp, 
  Check, 
  Binoculars, 
  Lightbulb,
  Database,
  FileText,
  Settings,
  Globe,
  Loader2
} from "lucide-react";

interface ToolOperationDisplayProps {
  content: string;
  type: "tool_call" | "tool_response";
}

type ParsedContent = {
  name?: string;
  timestamp?: string;
  content?: string | Record<string, any>;
  [key: string]: any;
} | string;

interface ToolOperation {
  id: string;
  callContent: string;
  responseContent: string | null;
  timestamp: number;
  expanded: boolean;
  responseExpanded: boolean;
}

// Per-message tool-operation store.
//
// This used to be a single module-level singleton shared by every
// ToolOperationDisplay in the whole app. Because it was never reset, its
// `toolOperations` array accumulated the tool blocks of every message and every
// conversation the user viewed in a page session — so the "(N operations)"
// banner showed a global running total (e.g. 234 spanning three different days
// and two different models) instead of the count for one assistant message, and
// it only ever grew as the user browsed.
//
// The store below has the SAME shape and behaviour as that singleton, but one
// instance is created per ToolOperationsProvider (i.e. per assistant message)
// and thrown away when that message unmounts. The count is therefore scoped to a
// single turn and is naturally bounded by the backend's ToolCallLimitMiddleware
// run_limit (60), so cross-message/cross-conversation leakage is impossible.
type ToolOperationsStore = {
  showDetails: boolean;
  callbacks: (() => void)[];
  toolOperations: ToolOperation[];
  hasResponse: boolean;
  lastRenderedType: "tool_call" | "tool_response" | null;
  toggle: () => void;
  addToolCall: (content: string) => string;
  addToolResponse: (content: string) => void;
  toggleExpanded: (id: string, isResponse?: boolean) => void;
  reset: () => void;
};

const createToolOperationsStore = (): ToolOperationsStore => ({
  showDetails: false,
  callbacks: [] as (() => void)[],
  toolOperations: [] as ToolOperation[],
  hasResponse: false,
  lastRenderedType: null as "tool_call" | "tool_response" | null,

  toggle: function() {
    this.showDetails = !this.showDetails;
    this.callbacks.forEach(callback => callback());
  },

  addToolCall: function(content: string) {
    const exists = this.toolOperations.some(op => op.callContent === content);
    if (exists) {
      return this.toolOperations.find(op => op.callContent === content)!.id;
    }

    const id = `tool_${Date.now()}_${Math.random()}`;
    this.toolOperations.push({
      id,
      callContent: content,
      responseContent: null,
      timestamp: Date.now(),
      expanded: false,
      responseExpanded: false
    });
    this.callbacks.forEach(callback => callback());
    return id;
  },

  addToolResponse: function(content: string) {
    const alreadyHasResponse = this.toolOperations.some(op => op.responseContent === content);
    if (alreadyHasResponse) {
      return;
    }

    // Find the most recent pending call (last one without a response)
    const pendingCalls = this.toolOperations.filter(op => op.responseContent === null);
    const pendingCall = pendingCalls[pendingCalls.length - 1]; // Get the LAST pending call

    if (pendingCall) {
      pendingCall.responseContent = content;
      this.hasResponse = true;
      this.callbacks.forEach(callback => callback());
    } else {
      // No pending tool_call, create a standalone response operation
      console.warn("⚠️ No pending tool call found, creating standalone response");
      const id = `tool_${Date.now()}_${Math.random()}`;
      this.toolOperations.push({
        id,
        callContent: '', // Empty call content
        responseContent: content,
        timestamp: Date.now(),
        expanded: false,
        responseExpanded: false
      });
      this.hasResponse = true;
      this.callbacks.forEach(callback => callback());
    }
  },

  toggleExpanded: function(id: string, isResponse: boolean = false) {
    const operation = this.toolOperations.find(op => op.id === id);
    if (operation) {
      if (isResponse) {
        operation.responseExpanded = !operation.responseExpanded;
      } else {
        operation.expanded = !operation.expanded;
      }
      this.callbacks.forEach(callback => callback());
    }
  },

  reset: function() {
    this.toolOperations = [];
    this.hasResponse = false;
    this.showDetails = false;
    this.lastRenderedType = null;
    this.callbacks.forEach(callback => callback());
  }
});

const ToolOperationsContext = React.createContext<ToolOperationsStore | null>(null);

// Wrap each assistant message's body in this so every ToolOperationDisplay it
// renders shares ONE store scoped to that message. One provider instance per
// message → counts reset naturally on unmount / conversation switch.
export const ToolOperationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storeRef = React.useRef<ToolOperationsStore | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createToolOperationsStore();
  }
  return (
    <ToolOperationsContext.Provider value={storeRef.current}>
      {children}
    </ToolOperationsContext.Provider>
  );
};

// Resolve the message-scoped store. If a ToolOperationDisplay is ever rendered
// outside a provider, fall back to a private per-instance store rather than
// crashing or leaking into a shared global.
const useToolOperationsStore = (): ToolOperationsStore => {
  const ctx = React.useContext(ToolOperationsContext);
  const fallbackRef = React.useRef<ToolOperationsStore | null>(null);
  if (ctx) {
    return ctx;
  }
  if (fallbackRef.current === null) {
    fallbackRef.current = createToolOperationsStore();
  }
  return fallbackRef.current;
};

const ToolOperationDisplay: React.FC<ToolOperationDisplayProps> = ({ content, type }) => {

  // Message-scoped store (was a module-level singleton). Every reference to
  // `globalState` below now reads/writes state for THIS message only.
  const globalState = useToolOperationsStore();

  const [copied, setCopied] = useState<boolean>(false);
  const [localShowDetails, setLocalShowDetails] = useState<boolean>(false);
  const [, forceUpdate] = useState({});
  const [expanded, setExpanded] = useState<boolean>(false);

  React.useEffect(() => {
    
    const updateLocal = () => {
      setLocalShowDetails(globalState.showDetails);
      forceUpdate({});
    };
    globalState.callbacks.push(updateLocal);
    
    if (type === 'tool_call') {
      globalState.addToolCall(content);
      globalState.lastRenderedType = 'tool_call';
  
    } else if (type === 'tool_response') {
      globalState.addToolResponse(content);
      globalState.lastRenderedType = 'tool_response';
      
    }
    
    return () => {
      const index = globalState.callbacks.indexOf(updateLocal);
      if (index > -1) {
        globalState.callbacks.splice(index, 1);
      }
    };
  }, [content, type]);

  const isMCPTool = useMemo(() => {
    try {
      const parsed = JSON.parse(content);
      
      const nameCheck = parsed.name && typeof parsed.name === 'string';
      const hasToolStructure = parsed.arguments || parsed.tool || parsed.function;
      
      const result = nameCheck || hasToolStructure;
      
      return result;
    } catch (e) {
      return false;
    }
  }, [content]);

  const getToolSummary = () => {
    const allOperations = globalState.toolOperations;
    const operationCount = allOperations.length;
    
    // Check if there are any pending operations (without responses)
    const hasPendingOperations = allOperations.some(op => op.responseContent === null);
    const hasAnyResponse = allOperations.some(op => op.responseContent !== null);
    
    const summary = {
      icon: hasPendingOperations ? (
        <Loader2 size={12} className="text-blue-600 animate-spin" />
      ) : (
        <Check size={12} className="text-green-600" />
      ),
      text: hasPendingOperations 
        ? "Gathering information - click here for more details"
        : `Information gathered${operationCount > 1 ? ` (${operationCount} operations)` : ''} - click here for more details`
    };
    return summary;
  };

  const renderToolPanel = (
    panelType: "tool_call" | "tool_response", 
    panelContent: string, 
    operationId: string,
    isExpanded: boolean
  ) => {
    let parsedContent: ParsedContent;
    try {
      parsedContent = JSON.parse(panelContent);
    } catch (e) {
      parsedContent = panelContent;
    }

    if (panelType === "tool_response" && 
        typeof parsedContent !== 'string' && 
        parsedContent.content && 
        typeof parsedContent.content === 'string') {
      try {
        const innerContent = JSON.parse(parsedContent.content);
        parsedContent.content = innerContent;
      } catch (e) {
        // Keep as string if parsing fails
      }
    }

    const formattedJson = typeof parsedContent === "object"
      ? JSON.stringify(parsedContent, null, 2)
      : String(parsedContent);

    const getHeaderTitle = () => {
      return panelType === "tool_call" ? "Gathering insights..." : "Insights gathered";
    };

    const getStatusIcon = () => {
      return panelType === "tool_call" ? (
        <Binoculars size={14} className="text-blue-600" />
      ) : (
        <Lightbulb size={14} className="text-yellow-500" />
      );
    };

    const copyToClipboard = () => {
      navigator.clipboard.writeText(formattedJson).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };

    return (
      <div className="mx-auto sm:max-w-3xl md:max-w-2xl lg:max-w-3xl text-xs lg:text-sm rounded-lg">
        <div className="rounded-md border">
          <div className="flex items-center relative px-4 py-3 text-xs font-sans justify-between rounded-md bg-white">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {getStatusIcon()}
                <span className="font-medium text-gray-700">
                  {getHeaderTitle()}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                className="flex gap-1 items-center hover:bg-white/50 px-2 py-1 rounded transition-colors"
                onClick={() => globalState.toggleExpanded(operationId, panelType === "tool_response")}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp size={12} />
                    <span className="hidden sm:inline">Collapse</span>
                  </>
                ) : (
                  <>
                    <ChevronDown size={12} />
                    <span className="hidden sm:inline">Expand</span>
                  </>
                )}
              </button>
              
              <button
                className="flex gap-1 items-center hover:bg-white/50 px-2 py-1 rounded transition-colors"
                onClick={copyToClipboard}
              >
                {copied ? (
                  <>
                    <CopyCheck size={12} />
                    <span className="hidden sm:inline">Copied!</span>
                  </>
                ) : (
                  <>
                    <CopyIcon size={12} />
                    <span className="hidden sm:inline">Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          {isExpanded && (
            <div className="overflow-hidden">
              <SyntaxHighlighter
                language="json"
                style={tomorrow}
                customStyle={{
                  margin: 0,
                  padding: "1rem",
                  borderRadius: "0 0 8px 8px",
                  maxHeight: "400px",
                  overflow: "auto",
                  fontSize: "12px",
                  lineHeight: "1.4"
                }}
                showLineNumbers={true}
                wrapLines={true}
              >
                {formattedJson}
              </SyntaxHighlighter>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Every tool_call / tool_response code block in a message mounts its OWN
  // ToolOperationDisplay, but they all share one message-scoped store and the
  // aggregated view below lists ALL operations in that store. So exactly one
  // instance — the "anchor" — may render it; every other instance must return
  // null, or the whole list is drawn once per instance.
  //
  // The anchor is the instance owning the newest rendered piece of the LAST
  // operation: its response once one has arrived, otherwise its call.
  //
  // Both the collapsed pill and the expanded panel MUST use this same
  // predicate. They previously computed it separately, and the expanded
  // branch's version accepted "last op's call OR last op's response" — a
  // condition BOTH instances satisfy once the response arrives. That is why
  // the pill appeared once but expanding it showed the operations twice.
  const isAnchorInstance = (() => {
    const allOperations = globalState.toolOperations;
    const lastOperation = allOperations[allOperations.length - 1];
    if (!lastOperation) {
      return false;
    }
    return lastOperation.responseContent !== null
      ? type === "tool_response" && lastOperation.responseContent === content
      : type === "tool_call" && lastOperation.callContent === content;
  })();

  // For MCP tools
  if (isMCPTool) {
    const toolSummary = getToolSummary();

    if (!localShowDetails) {

      if (isAnchorInstance) {
        return (
          <div className="my-2">
            <button
              onClick={() => {
                globalState.toggle();
              }}
              className="flex items-center space-x-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-full transition-colors"
              title="Show tool operations"
            >
              {toolSummary.icon}
              <span>{toolSummary.text}</span>
            </button>
          </div>
        );
      }
      return null;
    }
    
    // When expanded, show ALL operations — from the anchor instance only.
    if (!isAnchorInstance) {
      return null;
    }

    return (
      <div className="my-4">
        {/* Hide button */}
        <div className="flex justify-center mb-3">
          <button
            onClick={() => globalState.toggle()}
            className="text-xs text-gray-500 hover:text-gray-700 flex items-center space-x-1 px-3 py-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ChevronUp size={12} />
            <span>Hide tool operations</span>
          </button>
        </div>

        {/* Render all tool operations */}
        {globalState.toolOperations.map((operation, index) => (
          <div key={operation.id} className="mb-4">
            {/* Operation header */}
            {globalState.toolOperations.length > 1 && (
              <div className="text-xs text-gray-500 mb-2 px-2 font-medium">
                Operation {index + 1} of {globalState.toolOperations.length}
              </div>
            )}
            
            {/* Tool Call Panel - always show, even if empty callContent */}
            <div className="mb-2">
              {operation.callContent ? (
                renderToolPanel(
                  "tool_call", 
                  operation.callContent, 
                  operation.id,
                  operation.expanded
                )
              ) : operation.responseContent ? (
                // Extract tool call info from response if callContent is empty
                (() => {
                  try {
                    const responseData = JSON.parse(operation.responseContent);
                    // Create a pseudo tool_call from the response metadata
                    const pseudoCall = {
                      type: "tool_call",
                      name: responseData.name || "unknown_tool",
                      tool_call_id: responseData.tool_call_id,
                      // Add any other relevant fields from the response
                    };
                    return renderToolPanel(
                      "tool_call",
                      JSON.stringify(pseudoCall, null, 2),
                      operation.id,
                      operation.expanded
                    );
                  } catch (e) {
                    // Fallback to placeholder if parsing fails
                    return (
                      <div className="mx-auto sm:max-w-3xl md:max-w-2xl lg:max-w-3xl text-xs lg:text-sm rounded-lg">
                        <div className="rounded-md border">
                          <div className="flex items-center relative px-4 py-3 text-xs font-sans justify-between rounded-md bg-white">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2">
                                <Binoculars size={14} className="text-blue-600" />
                                <span className="font-medium text-gray-700">
                                  Gathering insights...
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                })()
              ) : null}
            </div>

            {/* Tool Response Panel (if available) */}
            {operation.responseContent && (
              <div className="mb-2">
                {renderToolPanel(
                  "tool_response", 
                  operation.responseContent, 
                  operation.id,
                  operation.responseExpanded
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Suppress Path B for tool_call / tool_response code blocks.
  // These ALWAYS belong to the aggregating Path A above. If isMCPTool is
  // false here (e.g., JSON is still streaming and partial, or the payload
  // shape didn't match the MCP heuristic), do NOT render a standalone
  // widget — return null and let the next render pick it up once globalState
  // is consistent. Otherwise users see a duplicate "Gathering insights..."
  // panel alongside the aggregated summary pill.
  if (type === "tool_call" || type === "tool_response") {
    return null;
  }

  // Original behavior for non-MCP tools
  let parsedContent: ParsedContent;
  try {
    parsedContent = JSON.parse(content);
  } catch (e) {
    parsedContent = content;
  }

  if (type === "tool_response" && 
      typeof parsedContent !== 'string' && 
      parsedContent.content && 
      typeof parsedContent.content === 'string') {
    try {
      const innerContent = JSON.parse(parsedContent.content);
      parsedContent.content = innerContent;
    } catch (e) {
      // Keep as string if parsing fails
    }
  }

  const formattedJson = typeof parsedContent === "object"
    ? JSON.stringify(parsedContent, null, 2)
    : String(parsedContent);

  const getHeaderTitle = () => {
    return type === "tool_call" ? "Gathering insights..." : "Insights gathered";
  };

  const getStatusIcon = () => {
    return type === "tool_call" ? (
      <Binoculars size={14} className="text-blue-600" />
    ) : (
      <Lightbulb size={14} className="text-yellow-500" />
    );
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(formattedJson).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mx-auto sm:max-w-3xl md:max-w-2xl lg:max-w-3xl text-xs lg:text-sm rounded-lg my-4">
      <div className="rounded-md border">
        <div className={`flex items-center relative px-4 py-3 text-xs font-sans justify-between rounded-md bg-white`}>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              {getStatusIcon()}
              <span className="font-medium text-gray-700">
                {getHeaderTitle()}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              className="flex gap-1 items-center hover:bg-white/50 px-2 py-1 rounded transition-colors"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <ChevronUp size={12} />
                  <span className="hidden sm:inline">Collapse</span>
                </>
              ) : (
                <>
                  <ChevronDown size={12} />
                  <span className="hidden sm:inline">Expand</span>
                </>
              )}
            </button>
            
            <button
              className="flex gap-1 items-center hover:bg-white/50 px-2 py-1 rounded transition-colors"
              onClick={copyToClipboard}
            >
              {copied ? (
                <>
                  <CopyCheck size={12} />
                  <span className="hidden sm:inline">Copied!</span>
                </>
              ) : (
                <>
                  <CopyIcon size={12} />
                  <span className="hidden sm:inline">Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {expanded && (
          <div className="overflow-hidden">
            <SyntaxHighlighter
              language="json"
              style={tomorrow}
              customStyle={{
                margin: 0,
                padding: "1rem",
                borderRadius: "0 0 8px 8px",
                maxHeight: "400px",
                overflow: "auto",
                fontSize: "12px",
                lineHeight: "1.4"
              }}
              showLineNumbers={true}
              wrapLines={true}
            >
              {formattedJson}
            </SyntaxHighlighter>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolOperationDisplay;